<template>
  <tippy
    ref="tippyRef"
    trigger="manual"
    :placement="placement"
    class="flex [&>button]:overflow-hidden"
    :class="disabled && '[&>button]:cursor-not-allowed'"
    :style="{ maxWidth }"
    :arrow="false"
    :append-to="appendTo"
    :theme="tippyTheme"
    :popper-options="popperOptions"
    interactive
    tabindex="-1"
    :data-testid="testId"
  >
    <global-events
      v-if="isVisible"
      no-filtering
      @keydown.prevent.esc="() => menuState.closeMenu()"
      @keydown.prevent.enter="() => menuState.selectFocusedItem()"
      @keydown.space="handleSpace"
      @keydown.prevent.tab="handleTab"
      @keyup.prevent.tab
      @keydown.prevent.down="() => menuState.focusNext()"
      @keydown.prevent.up="() => menuState.focusPrevious()"
    />

    <slot />

    <template #content>
      <div v-if="isVisible" data-testid="dropdown-content">
        <slot name="before" />

        <div ref="listRef" :style="{ maxHeight: `${maxHeight}px` }" class="overflow-y-auto">
          <slot name="list" />
        </div>

        <slot name="after" />
      </div>
    </template>
  </tippy>
</template>

<script setup lang="ts">
import { computed, ref, provide, watch, onMounted } from 'vue'
import { Tippy, type TippyComponent } from 'vue-tippy'
import { type Placement } from 'tippy.js'
import { useMutationObserver } from '@vueuse/core'

import GlobalEvents from '../GlobalEvents.vue'

import { MENU_ITEM_CLASS } from './constants'
import { sameWidth } from './popper-modifiers'
import useMenu from './useMenu'

const props = withDefaults(
  defineProps<{
    disabled?: boolean
    placement?: Placement
    maxWidth?: string
    appendToDocument?: boolean
    size?: string
    matchWidth?: boolean
    maxHeight?: number
    allowSelectionWithSpace?: boolean
    testId?: string
  }>(),
  {
    allowSelectionWithSpace: true,
    maxHeight: 300,
    size: 'md',
    placement: 'bottom-start',
    maxWidth: '250px',
    testId: 'dropdown',
  },
)

const emit = defineEmits<{
  'visibility-change': [isOpen: boolean]
}>()

const tippyRef = ref<TippyComponent | null>(null)
const listRef = ref<HTMLDivElement | null>(null)

const menuState = useMenu(tippyRef)

const updateItemIds = () => {
  if (!listRef.value) return

  const menuItems = listRef.value.getElementsByClassName(MENU_ITEM_CLASS)
  const ids = Array.from(menuItems).map((item) => item.id)
  menuState.setItemIds(ids)
}

useMutationObserver(listRef, () => updateItemIds(), { childList: true, subtree: true })

const tippyTheme = computed(() => {
  const themes = ['light', 'dropdown', `dropdown-${props.size}`]
  return themes.join(' ')
})

const appendTo = computed(() => (props.appendToDocument ? document.body : 'parent'))

const popperOptions = computed(() => ({
  modifiers: props.matchWidth ? [sameWidth] : [],
}))

const handleTab = (e: KeyboardEvent) => {
  if (e.shiftKey) {
    menuState.focusPrevious()
  } else {
    menuState.focusNext()
  }
}

const handleSpace = (e: KeyboardEvent) => {
  if (props.allowSelectionWithSpace) {
    e.preventDefault()
    menuState.selectFocusedItem()
  }
}

const resetScroll = () => menuState.resetScroll()

const show = () => menuState.openMenu()
const hide = () => menuState.closeMenu()

const isVisible = computed(() => menuState.isVisible())

watch(isVisible, (visible) => {
  if (!visible) {
    menuState.resetScroll()
  }

  emit('visibility-change', visible)
})

onMounted(() => updateItemIds())

provide('menuState', menuState)

defineExpose({ tippyRef, resetScroll, show, hide, isVisible })
</script>

<style lang="scss" module>
:global(.tippy-box[data-theme~='dropdown'][role='tooltip']) {
  background-color: var(--white);
  width: 212px;
  box-shadow: theme('boxShadow.md');
  border-radius: 8px;
  color: var(--gray-80);
  padding: 4px 0;
  text-align: left;
}

:global(.tippy-box[data-theme~='dropdown-sm'][role='tooltip']) {
  width: 115px;
}

:global(.tippy-box[data-theme~='dropdown-lg'][role='tooltip']) {
  width: 320px;
}

:global(.tippy-box[data-theme~='dropdown-full'][role='tooltip']) {
  width: 32rem;
  max-width: none !important;
}

:global(.tippy-box[data-theme~='dropdown-fit'][role='tooltip']) {
  width: fit-content;
}

:global(.tippy-box[data-theme~='dropdown-match'][role='tooltip']) {
  width: 100%;
  max-width: none !important;
}

:global(.tippy-box[data-theme~='dropdown-sm-fit'][role='tooltip']) {
  min-width: 112px;
  width: fit-content;
}
</style>
